import React, { useEffect } from "react";
import LeaderboardEmployeeTable from "./components/leaderboardEmployeeTable";
import EmployeeCard from "./components/leaderboardEmployeeCard";
import {
  Box,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Pagination
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployeePerformanceOverview } from "../../store/reducers/employeePerformanceOverview";
import { fetchLeaderBoard } from "../../store/reducers/leaderBoard";
import { Link } from "react-router-dom";

const scoringRulesPath = "/badges";
const currentPointsNote =
  "Your points reflects your current level of learning and practice. Learning and practicing increase your score. If you're inactive for a while, your score gradually decreases as your skills and knowledge fade over time.";
const badgesEarnedNote =
  "Each badge is time-stamped and captures your score level at that moment. Every 14 days, your current points are evaluated and a new badge is awarded. Keep practicing to earn the next one.";

const columns = [
  { id: "employee", label: "Employee" },
  { id: "award_starting_date", label: "Award Starting Date" },
  { id: "readings", label: "Readings" },
  { id: "trainings", label: "Micro Trainings" },
  { id: "total_points", label: "Current points*" },
  { id: "badges", label: "Badges Earned**" },
];


const LeaderBoard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = React.useState(1);
  const itemsPerPage = 10;
  const organizationId = localStorage.getItem("organisation_id");
  const { data: leaderboardData, loading, error } = useSelector(
    (state) => state.leaderBoard
  );
  const data = leaderboardData.slice(0, 5);
  const pageCount = Math.ceil(data.length / itemsPerPage);
  const dispatch = useDispatch()
  const paginatedRows = data.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchLeaderBoard(+organizationId));
    }
  }, [organizationId, dispatch]);

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  return (
    <div
      style={{
        paddingBottom: `${isMobile ? "1rem" : ""}`,
        paddingTop: "1rem",
      }}
    >
      <div
        style={{
          padding: isMobile ? "0" : "2rem",
          backgroundColor: isMobile ? "transparent" : "var(--org-bg-tint-4, #fff0f1)",
          borderRadius: isMobile ? "0" : "16px",
          minHeight: isMobile ? "" : "90vh",
        }}
      >
        <h3
          style={{
            fontWeight: "600",
            color: "#2B2B2B",
            marginBottom: isMobile ? "1rem" : "2rem",
            fontSize: isMobile ? "18px" : "24px",
          }}
        >
          Top 5 performers of the Month
        </h3>

        {isMobile ? (
          <div>
            {!loading && data.length > 0 && (
              <Box
                mb={2}
                sx={{
                  backgroundColor: "#fff",
                  border: "1px solid rgba(204, 204, 204, 1)",
                  borderRadius: "10px",
                  p: 2,
                  fontSize: "13px",
                  lineHeight: 1.5,
                  fontStyle: "italic",
                  fontWeight: 600,
                  boxShadow: "0px 4px 18px rgba(0, 0, 0, 0.08)",
                }}
              >
                <p>
                  <strong><em>* Current points:</em></strong> {currentPointsNote}
                </p>
                <p>
                  <strong><em>** Badges Earned:</em></strong> {badgesEarnedNote}
                </p>
                <p style={{ marginBottom: 0 }}>
                  <Link
                    to={scoringRulesPath}
                    style={{
                      color: "var(--org-bg-color, #f6615f)",
                      fontStyle: "italic",
                      fontWeight: 700,
                    }}
                  >
                    Read more about scoring rules here
                  </Link>
                </p>
              </Box>
            )}

            {loading ? <Box
              height="64vh"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <div className="spinner-border text-danger" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </Box> : <Grid container spacing={1}>
              {paginatedRows.map((employee, index) => (
                <Grid item xs={12} key={employee.id}>
                  <EmployeeCard
                    data={employee}
                    index={(page - 1) * itemsPerPage + index}
                  />
                </Grid>
              ))}
            </Grid>}

            {data.length > 10 && (
              <Box mt={2} display="flex" justifyContent="center">
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={handleChangePage}
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "var(--org-bg-color, #f6615f) !important",
                      color: "#fff !important",
                      "&:hover": {
                        backgroundColor: "var(--org-bg-color, #f6615f) !important",
                      },
                    },
                  }}
                />
              </Box>
            )}
          </div>
        ) : (
          <>
            {!loading && data.length > 0 && (
              <Box
                mb={2}
                sx={{
                  backgroundColor: "#fff",
                  border: "1px solid rgba(204, 204, 204, 1)",
                  borderRadius: "10px",
                  p: 3,
                  fontSize: "14px",
                  lineHeight: 1.5,
                  fontStyle: "italic",
                  fontWeight: 600,
                  boxShadow: "0px 4px 18px rgba(0, 0, 0, 0.08)",
                }}
              >
                <Typography fontSize={14} mb={0.75}>
                  <strong><em>* Current points:</em></strong> {currentPointsNote}
                </Typography>
                <Typography fontSize={14} mb={0.75}>
                  <strong><em>** Badges Earned:</em></strong> {badgesEarnedNote}
                </Typography>
                <Typography fontSize={14}>
                  <Link
                    to={scoringRulesPath}
                    style={{
                      color: "var(--org-bg-color, #f6615f)",
                      fontStyle: "italic",
                      fontWeight: 700,
                    }}
                  >
                    Read more about scoring rules here
                  </Link>
                </Typography>
              </Box>
            )}
            <LeaderboardEmployeeTable
              loading={loading}
              columns={columns}
              rows={data}
              title={"Leaderboard"}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderBoard;
